import { addDays, addWeeks } from "date-fns";
import moment from "moment";
import { Await, useRouteLoaderData } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { Col, Row, Stack } from "react-bootstrap";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Shift } from "react-bootstrap-icons";
import DataDescription from "../components/DataDescription";
import { useLatestDate, useOnKeyEventPress, useResolvedModules } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatePicker, { lastMonth, lastWeek, today } from "../components/Pickers/DatePicker";
import DemoPicker from "../components/Pickers/DemoPicker";
import HourPicker from "../components/Pickers/HourPicker";
import StationPicker from "../components/Pickers/StationPicker";
import RATooltip from "../components/RATooltip";
import { useUserFormState } from "../components/UserSelection";
import ClocksData from "../components/clocks/ClocksData";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";

const extraStationsCount = 2;
const extraDefaults = Object.assign(
    {},
    ...Array(extraStationsCount)
        .fill()
        .map((_, i) => ({ [`extraStation${i}`]: [], [`extraDemo${i}`]: [] }))
);

export default function Clocks() {
    const moduleName = useMemo(() => "clocks", []);
    const latestCalculatedDate = useMemo(() => moment(), []);
    const { radiosModuleSettings } = useRouteLoaderData("root");

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [mainData, setMainData] = useState(null);
    const [excludedDays, setExcludedDays] = useState(null);
    const [submittedFormData, setSubmittedFormData] = useState(null);

    const resolvedModules = useResolvedModules();
    const ratingsEnabled = useMemo(() => resolvedModules?.includes(2), [resolvedModules]);

    const container = useRef(null);
    const advancedInputRef = useRef(null);

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            return [today(), lastWeek(latestCalculatedDate), lastMonth(latestCalculatedDate)];
        }
    }, [latestCalculatedDate]);

    useLatestDate(setFormData, formData.date, latestCalculatedDate);

    useEffect(() => {
        async function saveForm() {
            await saveFormData();
            await saveFormData(formData.station[0].radio_id);
        }
        if (refreshCounter >= 1 && formData.station.length) saveForm();
    }, [formData.station, refreshCounter, saveFormData]);

    async function handleFormSubmit(event, date, hour) {
        event.preventDefault();
        if (!formData.station.length || !(date ?? formData.date)) return;
        advancedInputRef.current.closeAdvanced();
        setSubmittedFormData({
            ...formData,
            addToCollectionAllowed: formData.station[0].client && !formData.station[0].mtt_only,
            radioId: formData.station[0].radio_id,
            date: date ?? formData.date,
            hour: hour ?? formData.hour,
        });
        setRefreshCounter(prevCounter => prevCounter + 1);
    }

    async function previousHour(hours = 1) {
        if (formData.date) {
            const newHour = (24 + ((Number(formData.hour) - hours) % 24)) % 24;
            const newDate = Number(formData.hour) < hours ? addDays(formData.date, -1) : formData.date;
            setFormData(current => ({ ...current, date: newDate, hour: newHour }));
            await handleFormSubmit(new Event("Submit"), newDate, newHour);
        }
    }
    async function nextHour(hours = 1) {
        if (formData.date) {
            const newHour = (24 + ((Number(formData.hour) + hours) % 24)) % 24;
            const newDate = Number(formData.hour) + hours > 23 ? addDays(formData.date, 1) : formData.date;
            setFormData(current => ({ ...current, date: newDate, hour: newHour }));
            await handleFormSubmit(new Event("Submit"), newDate, newHour);
        }
    }
    async function previousDay() {
        if (formData.date) {
            const newDate = addDays(formData.date, -1);
            setFormData(current => ({ ...current, date: newDate }));
            await handleFormSubmit(new Event("Submit"), newDate);
        }
    }
    async function nextDay() {
        if (formData.date) {
            const newDate = addDays(formData.date, 1);
            setFormData(current => ({ ...current, date: newDate }));
            await handleFormSubmit(new Event("Submit"), newDate);
        }
    }
    async function previousWeek() {
        if (formData.date) {
            const newDate = addWeeks(formData.date, -1);
            setFormData(current => ({ ...current, date: newDate }));
            await handleFormSubmit(new Event("Submit"), newDate);
        }
    }
    async function nextWeek() {
        if (formData.date) {
            const newDate = addWeeks(formData.date, 1);
            setFormData(current => ({ ...current, date: newDate }));
            await handleFormSubmit(new Event("Submit"), newDate);
        }
    }
    useOnKeyEventPress(async () => await previousHour(4), "ArrowLeft", false, true);
    useOnKeyEventPress(async () => await nextHour(4), "ArrowRight", false, true);
    useOnKeyEventPress(previousHour, "ArrowLeft", false, false, ".clocks-container");
    useOnKeyEventPress(nextHour, "ArrowRight", false, false, ".clocks-container");
    useOnKeyEventPress(previousWeek, "ArrowDown", false, true, ".clocks-container");
    useOnKeyEventPress(nextWeek, "ArrowUp", false, true, ".clocks-container");
    useOnKeyEventPress(previousDay, "ArrowDown", false, false, ".clocks-container");
    useOnKeyEventPress(nextDay, "ArrowUp", false, false, ".clocks-container");

    const extraStations = useMemo(() => {
        if (!submittedFormData) return null;
        const stations = Array(extraStationsCount)
            .fill()
            .map((_, i) => {
                return { station: submittedFormData[`extraStation${i}`], demo: submittedFormData[`extraDemo${i}`] };
            })
            .filter(x => x.station.length);
        return stations;
    }, [submittedFormData]);

    return (
        <ModulePage>
            <ModulePage.Title>Clocks</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length || !formData.date || formData.hour === ""}>
                <InputSelection.Main>
                    <StationPicker formData={formData} setFormData={setFormData} name={moduleName} includeCompetitors loadOnChange={loadFormData} />
                    {ratingsEnabled && (
                        <Suspense>
                            <Await resolve={radiosModuleSettings}>
                                {resolvedModuleSettings => (
                                    <DemoPicker
                                        formData={formData}
                                        setFormData={setFormData}
                                        name={moduleName}
                                        disabled={!formData.station.length || formData.station[0]?.mtt_only}
                                        multiple
                                        filterFunction={x => {
                                            if (!formData.station.length) return false;
                                            const station = formData.station[0];
                                            const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                                r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled
                                            );
                                            return (
                                                (x.data_type === 1 && station.stream && (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                (x.data_type === 2 && station.ppm)
                                            );
                                        }}
                                    />
                                )}
                            </Await>
                        </Suspense>
                    )}
                    <DatePicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <HourPicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <Suspense>
                    <Await resolve={radiosModuleSettings}>
                        {resolvedModuleSettings => (
                            <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                                {Array(extraStationsCount)
                                    .fill()
                                    .map((_, i) => {
                                        const station = formData[`extraStation${i}`][0];

                                        return (
                                            <Col xs={12} key={i}>
                                                <Row>
                                                    <StationPicker
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        name={moduleName}
                                                        filterFunction={x =>
                                                            ![
                                                                formData.station[0]?.radio_id,
                                                                formData.extraStation0[0]?.radio_id,
                                                                formData.extraStation1[0]?.radio_id,
                                                            ].includes(x.radio_id)
                                                        }
                                                        includeCompetitors
                                                        formKey={`extraStation${i}`}
                                                        demoKey={`extraDemo${i}`}
                                                        stationKey={`extraStation${i}`}
                                                        label={`Station #${i + 2}`}
                                                    />
                                                    {ratingsEnabled && (
                                                        <DemoPicker
                                                            formData={formData}
                                                            setFormData={setFormData}
                                                            name={moduleName}
                                                            disabled={!formData[`extraStation${i}`].length || formData[`extraStation${i}`][0]?.mtt_only}
                                                            formKey={`extraDemo${i}`}
                                                            stationKey={`extraStation${i}`}
                                                            label={`Demographic #${i + 2}`}
                                                            filterFunction={x => {
                                                                if (!station) return false;
                                                                const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                                                    r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled
                                                                );
                                                                return (
                                                                    (x.data_type === 1 &&
                                                                        station.stream &&
                                                                        (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                                    (x.data_type === 2 && station.ppm)
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </Row>
                                            </Col>
                                        );
                                    })}
                            </InputSelection.Advanced>
                        )}
                    </Await>
                </Suspense>
            </ModulePage.InputSelection>
            <ModulePage.DataContent>
                <DataContent.Placeholder show={!submittedFormData}>
                    <PageDesciption
                        title={"Clocks"}
                        whatList={[
                            "See a clock view of all music and talk elements in an hour and listen to the audio",
                            "Check any station in your station list (not all stations have audio recorded)",
                            "Commercials, news, and other details break info is displayed for your own station",
                        ]}
                        knowList={[
                            { text: "Audio is stored for 3 months max" },
                            { text: "Under advanced options you can choose up 2 more stations to compare with on one screen" },
                            {
                                text: "Right click to see all clock functions and their keyboard shortcuts. Touch devices display additional buttons for navigation",
                            },
                        ]}
                        relatedLinks={[
                            {
                                title: "Radio Visualized",
                                url: "https://radioanalyzer.com/blog/radio-visualized",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/vYgEFwqz9Mc?si=hPuRnX2Z4wCS9cHo"
                    />
                </DataContent.Placeholder>
                {submittedFormData && (
                    <>
                        <DescriptionWithDownloadRow
                            screenshotContainer={container}
                            filename={`Clocks ${submittedFormData.station[0].radio_name ?? ""} ${moment(submittedFormData.date).format("DD-MM-YYYY")}`}
                            data={mainData}>
                            <DataDescription>
                                {(mainData?.aa_status === 4 || mainData?.aa_status === -11) && (
                                    <DataDescription.Simple label="AA" value={mainData.aa_status === 4 ? "Processed" : "Processing"} />
                                )}
                                <DataDescription.ExcludedDays excludedDays={excludedDays} />
                                <DataDescription.Custom>
                                    <RATooltip size={30} className=" ms-auto">
                                        <p className="mb-3 text-ra-dark-blue">Control clocks using your keyboard.</p>
                                        <Row className=" gx-2 mb-2 fw-bold">
                                            <Col xs={12} className=" text-center">
                                                <span>Next Day</span>
                                            </Col>
                                            <Col className=" align-self-end text-end">
                                                <span>Previous hour</span>
                                            </Col>
                                            <Col xs={"auto"}>
                                                <Stack direction="horizontal" gap={1} className=" align-items-end d-inline-flex">
                                                    <span className="context-menu-key text-dark-emphasis">
                                                        <ArrowLeft />
                                                    </span>
                                                    <Stack gap={1}>
                                                        <span className="context-menu-key text-dark-emphasis">
                                                            <ArrowUp />
                                                        </span>
                                                        <span className="context-menu-key text-dark-emphasis">
                                                            <ArrowDown />
                                                        </span>
                                                    </Stack>
                                                    <span className="context-menu-key text-dark-emphasis">
                                                        <ArrowRight />
                                                    </span>
                                                </Stack>
                                            </Col>
                                            <Col className=" align-self-end">
                                                <span>Next hour</span>
                                            </Col>
                                            <Col xs={12} className=" text-center">
                                                <span>Previous Day</span>
                                            </Col>
                                        </Row>
                                        <Row className=" justify-content-center">
                                            <Col xs={"auto"}>
                                                <Stack direction="horizontal" gap={1}>
                                                    <span className="context-menu-key text-dark-emphasis pe-4">
                                                        <Shift />
                                                    </span>
                                                    <span className="fs-8 text-dark-emphasis">+</span>
                                                    <span className="context-menu-key text-dark-emphasis">
                                                        <ArrowUp />
                                                    </span>
                                                    <span>Next Week</span>
                                                </Stack>
                                                <Stack direction="horizontal" gap={1}>
                                                    <span className="context-menu-key text-dark-emphasis pe-4">
                                                        <Shift />
                                                    </span>
                                                    <span className="fs-8 text-dark-emphasis">+</span>
                                                    <span className="context-menu-key text-dark-emphasis">
                                                        <ArrowDown />
                                                    </span>
                                                    <span>Previous Week</span>
                                                </Stack>
                                            </Col>
                                        </Row>
                                        <p className="mt-3 mb-0 text-ra-dark-blue">Rigth click on clocks to show more features and shortcuts.</p>
                                    </RATooltip>
                                </DataDescription.Custom>
                            </DataDescription>
                        </DescriptionWithDownloadRow>
                        <div ref={container}>
                            <ClocksData
                                radioId={submittedFormData.radioId}
                                demo={submittedFormData.demo}
                                dataType={submittedFormData.station[0].stream ? 1 : 2}
                                date={submittedFormData.date}
                                hour={submittedFormData.hour}
                                refreshCounter={refreshCounter}
                                setMainData={setMainData}
                                setExcludedDays={setExcludedDays}
                                nextHour={nextHour}
                                previousHour={previousHour}
                                nextDay={nextDay}
                                previousDay={previousDay}
                                addToCollectionAllowed={submittedFormData.addToCollectionAllowed}
                            />
                            {extraStations?.length > 0 && (
                                <Row className="mt-1 flex-grow-1 clocks-secondary">
                                    {extraStations.map(x => (
                                        <Col key={x.station[0].radio_id} xl={6}>
                                            <ClocksData
                                                radioId={x.station[0].radio_id}
                                                demo={x.demo}
                                                dataType={x.station[0].stream ? 1 : 2}
                                                date={submittedFormData.date}
                                                hour={submittedFormData.hour}
                                                refreshCounter={refreshCounter}
                                                secondary={true}
                                                nextHour={nextHour}
                                                previousHour={previousHour}
                                                nextDay={nextDay}
                                                previousDay={previousDay}
                                                addToCollectionAllowed={x.station[0].client && !x.station[0].mtt_only}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                    </>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
